import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { formatTakaNo } from '../utils/taka';

interface Group {
  id: number;
  name: string;
  buyerName?: string | null;
  takaCount: number;
  totalMeters: number;
}
interface Taka {
  id: number;
  takaNo: number;
  series: string | null;
  meter?: number | null;
  qualityDesiNo?: string | null;
  colour?: string | null;
}

const fmt = (n: number) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// ── New Group modal: name + optional buyer + pick ungrouped takas (individually or by range) ──
const NewGroupModal: React.FC<{
  seriesOptions: (string | null)[];
  onClose: () => void;
  onSubmit: (payload: { name: string; buyerName?: string; takaIds: number[] }) => void;
  pending: boolean;
}> = ({ seriesOptions, onClose, onSubmit, pending }) => {
  const [name, setName] = useState('');
  const [buyer, setBuyer] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [rSeries, setRSeries] = useState<string>('');
  const [rFrom, setRFrom] = useState('');
  const [rTo, setRTo] = useState('');

  const { data, isLoading } = useQuery<{ takaInventories: Taka[] }>({
    queryKey: ['ungroupedTakas'],
    queryFn: async () => apiService.get('/taka-inventory?ungrouped=true&pageSize=1000'),
  });
  const takas: Taka[] = data?.takaInventories || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return takas;
    return takas.filter((t) =>
      String(t.takaNo).includes(q) ||
      (t.qualityDesiNo || '').toLowerCase().includes(q) ||
      (t.colour || '').toLowerCase().includes(q)
    );
  }, [takas, search]);

  const toggle = (id: number) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Range helper: tick every ungrouped taka whose series+number fall in the range.
  const applyRange = () => {
    const from = Number(rFrom), to = Number(rTo);
    if (!rFrom || !rTo || Number.isNaN(from) || Number.isNaN(to)) { toast.error('Enter a valid From and To'); return; }
    const lo = Math.min(from, to), hi = Math.max(from, to);
    const seriesMatch = (s: string | null) => (rSeries === '' ? true : (s === null ? rSeries === 'Default' : s === rSeries));
    const ids = takas.filter((t) => seriesMatch(t.series) && t.takaNo >= lo && t.takaNo <= hi).map((t) => t.id);
    if (!ids.length) { toast('No ungrouped takas in that range'); return; }
    setSelected((prev) => { const next = new Set(prev); ids.forEach((id) => next.add(id)); return next; });
    toast.success(`${ids.length} taka(s) selected`);
  };

  const submit = () => {
    if (!name.trim()) { toast.error('Group name is required'); return; }
    if (!selected.size) { toast.error('Select at least one taka'); return; }
    onSubmit({ name: name.trim(), buyerName: buyer.trim() || undefined, takaIds: Array.from(selected) });
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8EBF1]">
          <h3 className="text-lg font-bold text-[#1B202A]">New Taka Group</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#F8F9FC] text-gray-500">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Group name <span className="text-red-500">*</span></label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. LOT-A / Party ready"
                className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Buyer (optional)</label>
              <input value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="for later dispatch"
                className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
            </div>
          </div>

          {/* Range helper */}
          <div className="rounded-lg border border-gray-200 p-3 bg-[#F8F9FC]">
            <p className="text-xs font-semibold text-gray-600 mb-2">Select by taka range</p>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 mb-1">SERIES</label>
                <select value={rSeries} onChange={(e) => setRSeries(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white">
                  <option value="">Any</option>
                  {seriesOptions.map((s) => <option key={String(s)} value={s === null ? 'Default' : s}>{s === null ? 'Default' : s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 mb-1">FROM #</label>
                <input value={rFrom} onChange={(e) => setRFrom(e.target.value)} type="number" className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 mb-1">TO #</label>
                <input value={rTo} onChange={(e) => setRTo(e.target.value)} type="number" className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
              </div>
              <button type="button" onClick={applyRange} className="px-3 py-1.5 text-sm font-semibold text-[#0055a9] bg-[#F0F3FF] border border-[#D4DBEF] rounded-lg hover:bg-[#E4E9FC]">Select range</button>
            </div>
          </div>

          {/* Ungrouped taka checklist */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-gray-600">Ungrouped takas {selected.size > 0 && <span className="text-blue-700">· {selected.size} selected</span>}</label>
              <div className="flex items-center gap-2 text-xs">
                <button type="button" onClick={() => setSelected(new Set(filtered.map((t) => t.id)))} className="font-semibold text-blue-600 hover:underline">Select all</button>
                <span className="text-gray-300">·</span>
                <button type="button" onClick={() => setSelected(new Set())} className="font-semibold text-gray-500 hover:underline">Clear</button>
              </div>
            </div>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search taka no / quality / colour…"
              className="block w-full mb-2 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
            <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 max-h-56 overflow-y-auto">
              {isLoading && <p className="text-xs text-gray-400 px-3 py-3 text-center">Loading…</p>}
              {!isLoading && !filtered.length && <p className="text-xs text-gray-400 px-3 py-3 text-center">No ungrouped takas.</p>}
              {filtered.map((t) => {
                const checked = selected.has(t.id);
                return (
                  <label key={t.id} className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer ${checked ? 'bg-blue-50/60' : 'hover:bg-gray-50'}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(t.id)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    <span className="text-sm font-semibold text-[#0E131B] tabular-nums w-16">{formatTakaNo(t.series, t.takaNo)}</span>
                    <span className="text-xs text-gray-500 truncate">{t.qualityDesiNo || '—'}{t.colour ? ` · ${t.colour}` : ''}{t.meter != null ? ` · ${fmt(t.meter)} m` : ''}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
          <button onClick={submit} disabled={pending} className="px-5 py-2 text-sm font-semibold text-white bg-[#0055a9] rounded-lg shadow-sm hover:bg-[#0B2278] disabled:opacity-50">
            {pending ? 'Creating…' : `Create Group${selected.size ? ` (${selected.size})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── One collapsible group section: header + (on expand) its takas with remove buttons ──
const GroupSection: React.FC<{
  group: Group;
  canWrite: boolean;
  onDelete: () => void;
  onRename: (name: string) => void;
  onRemoveTaka: (takaId: number) => void;
}> = ({ group, canWrite, onDelete, onRename, onRemoveTaka }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [nameVal, setNameVal] = useState(group.name);

  const { data, isLoading } = useQuery<{ takaInventories: Taka[] }>({
    queryKey: ['groupTakas', group.id],
    queryFn: async () => apiService.get(`/taka-inventory?groupId=${group.id}&pageSize=500`),
    enabled: open,
  });
  const takas: Taka[] = data?.takaInventories || [];

  return (
    <div className="border border-[#E8EBF1] rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2.5 bg-[#F8F9FC]">
        <button onClick={() => setOpen((o) => !o)} className="p-0.5 text-gray-500 hover:text-[#0E131B]">
          <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
        {editing ? (
          <input autoFocus value={nameVal} onChange={(e) => setNameVal(e.target.value)}
            onBlur={() => { setEditing(false); if (nameVal.trim() && nameVal.trim() !== group.name) onRename(nameVal.trim()); else setNameVal(group.name); }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { setNameVal(group.name); setEditing(false); } }}
            className="text-sm font-bold text-[#1B202A] border border-blue-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
        ) : (
          <button onClick={() => setOpen((o) => !o)} className="text-sm font-bold text-[#1B202A] text-left">{group.name}</button>
        )}
        {group.buyerName && <span className="text-xs text-gray-500">· {group.buyerName}</span>}
        <span className="ml-auto text-xs font-semibold text-[#0055a9] bg-white border border-[#D4DBEF] rounded-full px-2 py-0.5 whitespace-nowrap">
          {group.takaCount} taka{group.takaCount === 1 ? '' : 's'} · {fmt(group.totalMeters)} m
        </span>
        {canWrite && (
          <>
            <button onClick={() => { setEditing(true); setNameVal(group.name); }} title="Rename" className="p-1.5 rounded-lg text-gray-400 hover:text-[#0E131B] hover:bg-white">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            </button>
            <button onClick={onDelete} title="Delete group" className="p-1.5 rounded-lg text-[#E02D3C] hover:bg-red-50">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </button>
          </>
        )}
      </div>
      {open && (
        <div className="divide-y divide-gray-100">
          {isLoading && <p className="text-xs text-gray-400 px-3 py-3 text-center">Loading…</p>}
          {!isLoading && !takas.length && <p className="text-xs text-gray-400 px-3 py-3 text-center">No takas in this group.</p>}
          {takas.map((t) => (
            <div key={t.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50/60">
              <span className="text-sm font-semibold text-[#0E131B] tabular-nums w-16">{formatTakaNo(t.series, t.takaNo)}</span>
              <span className="text-xs text-gray-500 truncate">{t.qualityDesiNo || '—'}{t.colour ? ` · ${t.colour}` : ''}{t.meter != null ? ` · ${fmt(t.meter)} m` : ''}</span>
              {canWrite && (
                <button onClick={() => onRemoveTaka(t.id)} title="Remove from group" className="ml-auto p-1 rounded text-gray-400 hover:text-[#E02D3C] hover:bg-red-50">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Panel shown above the Ungrouped taka list: lists groups + create/manage them.
const TakaGroupsPanel: React.FC<{ canWrite: boolean; seriesOptions: (string | null)[] }> = ({ canWrite, seriesOptions }) => {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [deleting, setDeleting] = useState<Group | null>(null);

  const { data } = useQuery<{ groups: Group[] }>({ queryKey: ['takaGroups'], queryFn: async () => apiService.get('/taka-groups') });
  const groups: Group[] = data?.groups || [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['takaGroups'] });
    qc.invalidateQueries({ queryKey: ['takaInventories'] });
    qc.invalidateQueries({ queryKey: ['groupTakas'] });
    qc.invalidateQueries({ queryKey: ['ungroupedTakas'] });
  };

  const createMut = useMutation({
    mutationFn: (payload: any) => apiService.post('/taka-groups', payload),
    onSuccess: (d: any) => { toast.success(`Group created${d?.assigned != null ? ` · ${d.assigned} taka(s)` : ''}`); invalidate(); setModalOpen(false); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to create group'),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => apiService.delete(`/taka-groups/${id}`),
    onSuccess: () => { toast.success('Group deleted'); invalidate(); setDeleting(null); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to delete group'),
  });
  const renameMut = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => apiService.put(`/taka-groups/${id}`, { name }),
    onSuccess: () => { toast.success('Group renamed'); invalidate(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to rename'),
  });
  const removeTakaMut = useMutation({
    mutationFn: ({ id, takaId }: { id: number; takaId: number }) => apiService.delete(`/taka-groups/${id}/takas`, { data: { takaIds: [takaId] } }),
    onSuccess: () => { toast.success('Removed from group'); invalidate(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to remove'),
  });

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-[#1B202A]">Taka Groups {groups.length > 0 && <span className="text-[rgba(14,19,27,0.4)] font-medium">· {groups.length}</span>}</h3>
        {canWrite && (
          <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-1.5 bg-[#0055a9] text-white rounded-xl px-3 py-1.5 text-sm font-semibold hover:bg-[#0d2780] transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            New Group
          </button>
        )}
      </div>

      {groups.length === 0 ? (
        <p className="text-xs text-[rgba(14,19,27,0.4)] bg-[#F8F9FC] border border-[#E8EBF1] rounded-xl px-3 py-3">No groups yet. Select takas or a range and create one.</p>
      ) : (
        <div className="space-y-2">
          {groups.map((g) => (
            <GroupSection key={g.id} group={g} canWrite={canWrite}
              onDelete={() => setDeleting(g)}
              onRename={(name) => renameMut.mutate({ id: g.id, name })}
              onRemoveTaka={(takaId) => removeTakaMut.mutate({ id: g.id, takaId })}
            />
          ))}
        </div>
      )}

      {modalOpen && (
        <NewGroupModal seriesOptions={seriesOptions} onClose={() => setModalOpen(false)}
          onSubmit={(payload) => createMut.mutate(payload)} pending={createMut.isPending} />
      )}

      {deleting && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4" onClick={() => setDeleting(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#1B202A] mb-2">Delete group</h3>
            <p className="text-sm text-gray-600 mb-4">Delete <span className="font-semibold">{deleting.name}</span>? Its {deleting.takaCount} taka(s) go back to Ungrouped — no takas are deleted.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleting(null)} className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={() => deleteMut.mutate(deleting.id)} disabled={deleteMut.isPending} className="px-4 py-2 text-sm font-semibold text-white bg-[#E02D3C] rounded-lg hover:bg-[#c82633] disabled:opacity-50">
                {deleteMut.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TakaGroupsPanel;
